import { useTranslation } from 'react-i18next';
import { Table, type TableColumn } from '@bn-apps/dim-ui-react';
import { ArrowOutward } from '@bn-apps/design-system-icons';
import { openInYasgui } from '@utils/yasgui.utils';
import { getIntlLocale } from '@utils/locale.utils';
import { useAvailableViewportHeight } from '@hooks/useAvailableViewportHeight';
import type { KeyFigure } from '@/types';
import styles from './ScalarTable.module.scss';

interface ScalarTableProps {
  scalars: KeyFigure[];
}

// Format a UN/CEFACT unit code to a human-readable symbol
function formatUnit(code?: string): string {
  switch (code) {
    case 'KMT': return 'km';
    case 'C62': return '';
    default:    return code ?? '';
  }
}

function formatValue(value?: number): string {
  if (value === undefined) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function ScalarTable({ scalars }: ScalarTableProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const tableWrapperRef = useAvailableViewportHeight<HTMLDivElement>();

  const columns: TableColumn<KeyFigure>[] = [
    { id: 'metric', header: t('keyFigures.table.metric'), cell: (figure) => figure.title },
    {
      id: 'value',
      header: t('keyFigures.table.value'),
      numeric: true,
      width: '8rem',
      cell: (figure) => {
        if (!figure.distribution) {
          return <span className={styles.textSubtle}>{t('keyFigures.notCalculated')}</span>;
        }
        const unit = formatUnit(figure.unitCode);
        return `${formatValue(figure.distribution.value)}${unit ? ' ' + unit : ''}`;
      },
    },
    {
      id: 'updated',
      header: t('keyFigures.table.updated'),
      numeric: true,
      width: '8rem',
      cell: (figure) => (
        <span className={styles.date}>
          {figure.distribution
            ? figure.distribution.created.toLocaleDateString(getIntlLocale(i18n.language))
            : '—'}
        </span>
      ),
    },
    {
      id: 'action',
      header: t('keyFigures.table.action'),
      width: '5rem',
      cell: (figure) => (
        <button
          className={styles.iconButton}
          aria-label={t('datasets.openInYasgui.buttonAria', { title: figure.title })}
          title={t('datasets.openInYasgui.button')}
          onClick={() => openInYasgui(figure.sparqlQuery, figure.title)}
        >
          <ArrowOutward />
        </button>
      ),
    },
  ];

  // The hook sets --available-viewport-height here, so the table never scrolls past the window
  return (
    <div ref={tableWrapperRef}>
      <Table
        label={t('keyFigures.table.ariaLabel', 'Key figures table')}
        variant="plain"
        columns={columns}
        rows={scalars}
        rowKey={(figure) => figure.uri}
        minWidth="600px"
        maxHeight="min(600px, var(--available-viewport-height, 60dvh))"
      />
    </div>
  );
}

export default ScalarTable;
